/** Input validation utilities for API routes. */

/**
 * Validate email format.
 */
export function validateEmail(email: string): { valid: boolean; error?: string } {
  if (!email || typeof email !== 'string') {
    return { valid: false, error: 'Email is required' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { valid: false, error: 'Invalid email format' };
  }

  // Additional checks
  if (email.length > 254) {
    return { valid: false, error: 'Email is too long (max 254 characters)' };
  }

  return { valid: true };
}

/**
 * Validate password strength.
 * Requirements: minimum 12 characters, at least one letter and one number.
 * Complies with SAQ Requirement #19: User set passwords must be at least 12 characters in length.
 */
export function validatePassword(password: string): { valid: boolean; error?: string } {
  if (!password || typeof password !== 'string') {
    return { valid: false, error: 'Password is required' };
  }

  if (password.length < 12) {
    return { valid: false, error: 'Password must be at least 12 characters long' };
  }

  if (!/[a-zA-Z]/.test(password)) {
    return { valid: false, error: 'Password must contain at least one letter' };
  }

  if (!/[0-9]/.test(password)) {
    return { valid: false, error: 'Password must contain at least one number' };
  }

  if (password.length > 128) {
    return { valid: false, error: 'Password is too long (max 128 characters)' };
  }

  return { valid: true };
}

/**
 * Maximum number of `*` wildcards allowed in a single allow/blocklist pattern.
 *
 * A pattern with N wildcards and no other metacharacters compiles (via
 * `compileWildcardPattern`) to a regex shaped like `/^a.*a.*a.*...ZZZ$/i` — a classic
 * `(x.*)+`-style construction that is vulnerable to catastrophic backtracking when
 * tested against a non-matching string containing repeats of the literal characters.
 *
 * Benchmarked against a ~40-character adversarial input (repeated literal char, no
 * matching suffix): 3 wildcards ~0.08ms, 4 ~0.4ms, 5 ~2.5ms, 6 ~12ms, 7 ~43ms, 8
 * ~140ms, 9 ~420ms, 10 ~1000ms+ (growing roughly 3-5x per additional wildcard,
 * i.e. exponential). 3 is chosen because it stays two full orders of magnitude below
 * where the blowup becomes noticeable (6+), while still covering every real pattern
 * shape in this product (`*.example.com`, `*@example.com`, `user*@example.com`,
 * `user.name*@example.com`) — none of which need more than one or two wildcards.
 */
export const MAX_PATTERN_WILDCARDS = 3;

/**
 * Safely compile a glob-style wildcard pattern (using `*` as the wildcard) into a
 * case-insensitive, fully-anchored RegExp.
 *
 * Every regex metacharacter in the literal portions of the pattern is escaped, so a
 * pattern containing characters like `[`, `(`, `.`, etc. is matched literally rather
 * than being interpreted as regex syntax or throwing a `SyntaxError` from unbalanced
 * constructs (e.g. an unclosed `[`).
 *
 * Refuses to compile (returns `null`) when the pattern has more than
 * `MAX_PATTERN_WILDCARDS` wildcards, as defense in depth against catastrophic
 * backtracking (ReDoS): `validatePattern` below is expected to reject such patterns
 * before they are ever saved, but this guard protects `checkPatternMatch` (the hot,
 * synchronous scoring path) even if a pattern reaches storage through some other route
 * (e.g. a data migration, or a different insertion path that doesn't call
 * `validatePattern`).
 *
 * Shared by `checkPatternMatch` (apps/web/src/lib/scoring/heuristics.ts), which
 * compiles saved allowlist/blocklist patterns at scoring time, and `validatePattern`
 * below, which validates patterns at save time — keeping the compile logic in one
 * place avoids the two drifting apart.
 *
 * @param pattern - The pattern to compile, containing zero or more `*` wildcards.
 * @returns A RegExp matching the pattern, or `null` if compilation fails even after
 * escaping, or if the pattern exceeds the wildcard cap (defense in depth; callers
 * should fall back to a literal match).
 */
export function compileWildcardPattern(pattern: string): RegExp | null {
  const wildcardCount = pattern.split('*').length - 1;
  if (wildcardCount > MAX_PATTERN_WILDCARDS) {
    return null;
  }

  try {
    const escaped = pattern
      .split('*')
      .map((segment) => segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('.*');
    return new RegExp(`^${escaped}$`, 'i');
  } catch {
    return null;
  }
}

/**
 * Validate allowlist pattern (email or domain with optional wildcard).
 */
export function validatePattern(pattern: string): { valid: boolean; error?: string } {
  if (typeof pattern !== 'string') {
    return { valid: false, error: 'Pattern is required' };
  }

  // Trim whitespace
  const trimmed = pattern.trim();
  if (trimmed.length === 0) {
    return { valid: false, error: 'Pattern cannot be empty' };
  }

  if (trimmed.length > 255) {
    return { valid: false, error: 'Pattern is too long (max 255 characters)' };
  }

  // Reject patterns with too many wildcards before doing any shape/compile checks
  // (cheapest check first). A pattern with many `*` wildcards and no other
  // metacharacters compiles to a `.*`-repetition regex vulnerable to catastrophic
  // backtracking (ReDoS) — see MAX_PATTERN_WILDCARDS for the benchmark behind this
  // cap. This applies regardless of whether the pattern would otherwise compile fine.
  const wildcardCount = trimmed.split('*').length - 1;
  if (wildcardCount > MAX_PATTERN_WILDCARDS) {
    return {
      valid: false,
      error: `Pattern cannot contain more than ${MAX_PATTERN_WILDCARDS} wildcards`,
    };
  }

  // Check if it's a valid email pattern
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidShape = emailPattern.test(trimmed);

  // Check if it's a valid domain pattern (with optional wildcard)
  // Examples: example.com, *.example.com, *@example.com
  const domainPattern =
    /^(\*@)?(\*\.)?[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  const isValidDomainShape = domainPattern.test(trimmed);

  if (isValidShape || isValidDomainShape) {
    // The domain-wildcard branches ("*.example.com", "*@example.com") in
    // checkPatternMatch never call `new RegExp`, so only patterns that would hit the
    // general wildcard branch there (containing "*" but not starting with "*." or
    // "*@") need to be guarded here against failing to compile.
    const hitsGeneralWildcardBranch =
      trimmed.includes('*') && !trimmed.startsWith('*.') && !trimmed.startsWith('*@');
    if (hitsGeneralWildcardBranch && compileWildcardPattern(trimmed) === null) {
      return {
        valid: false,
        error: 'Pattern contains invalid characters and cannot be used',
      };
    }
    return { valid: true };
  }

  return {
    valid: false,
    error: 'Pattern must be a valid email or domain (with optional wildcard)',
  };
}

/**
 * Determines if a pattern is a sender (email) or domain entry.
 * @param pattern - The pattern to check
 * @returns 'sender' if pattern contains '@', 'domain' otherwise
 *
 * @example
 * ```typescript
 * getEntryType('user@example.com') // 'sender'
 * getEntryType('example.com') // 'domain'
 * getEntryType('*@example.com') // 'domain' (wildcard pattern)
 * ```
 */
export function getEntryType(pattern: string): 'sender' | 'domain' {
  return pattern.includes('@') ? 'sender' : 'domain';
}

/**
 * Validate threshold value (0-10).
 */
export function validateThreshold(threshold: number): { valid: boolean; error?: string } {
  if (typeof threshold !== 'number' || isNaN(threshold)) {
    return { valid: false, error: 'Threshold must be a number' };
  }

  if (threshold < 0 || threshold > 10) {
    return { valid: false, error: 'Threshold must be between 0 and 10' };
  }

  return { valid: true };
}

/**
 * Validate action preference.
 */
export function validateActionPreference(action: string): { valid: boolean; error?: string } {
  const validActions = ['label', 'label_archive', 'label_spam', 'spam', 'archive']; // Support legacy actions
  if (!validActions.includes(action)) {
    return {
      valid: false,
      error: `Action preference must be one of: ${validActions.join(', ')}`,
    };
  }
  return { valid: true };
}

/**
 * Validate pagination parameters.
 */
export function validatePagination(
  limit?: string | number,
  offset?: string | number
): { valid: boolean; limit?: number; offset?: number; error?: string } {
  // Parse limit
  let parsedLimit = 50; // default
  if (limit !== undefined) {
    const limitNum = typeof limit === 'string' ? parseInt(limit, 10) : limit;
    if (isNaN(limitNum) || limitNum < 1 || limitNum > 1000) {
      return { valid: false, error: 'Limit must be between 1 and 1000' };
    }
    parsedLimit = limitNum;
  }

  // Parse offset
  let parsedOffset = 0; // default
  if (offset !== undefined) {
    const offsetNum = typeof offset === 'string' ? parseInt(offset, 10) : offset;
    if (isNaN(offsetNum) || offsetNum < 0) {
      return { valid: false, error: 'Offset must be 0 or greater' };
    }
    parsedOffset = offsetNum;
  }

  return { valid: true, limit: parsedLimit, offset: parsedOffset };
}

/**
 * Validate request body size (max 1MB).
 */
export function validateBodySize(body: string): { valid: boolean; error?: string } {
  const maxSize = 1024 * 1024; // 1MB
  const bodySize = Buffer.byteLength(body, 'utf8');
  if (bodySize > maxSize) {
    return { valid: false, error: 'Request body is too large (max 1MB)' };
  }
  return { valid: true };
}

/**
 * Maximum length for User-Agent header in logs.
 * RFC 7230 recommends servers accept at least 8KB, but we limit to 500
 * for logging purposes to prevent log storage issues.
 */
const USER_AGENT_MAX_LENGTH = 500;

/**
 * Sanitize User-Agent header to prevent log injection and ensure safe logging.
 *
 * Security concerns addressed:
 * - Log injection via newlines/carriage returns
 * - Extremely long User-Agent strings (memory/log storage issues)
 * - Special characters that could break JSON serialization
 *
 * @param userAgent - Raw User-Agent header value (can be null/undefined)
 * @returns Sanitized User-Agent string or undefined if input is null/undefined/empty
 *
 * @example
 * ```typescript
 * const sanitized = sanitizeUserAgent(request.headers.get('user-agent'));
 * // Safe to log or send to Sentry
 * ```
 */
export function sanitizeUserAgent(userAgent: string | null | undefined): string | undefined {
  if (!userAgent || typeof userAgent !== 'string') {
    return undefined;
  }

  // Remove newlines and carriage returns to prevent log injection
  // Replace with space to preserve readability
  let sanitized = userAgent.replace(/[\r\n]+/g, ' ');

  // Truncate if too long
  if (sanitized.length > USER_AGENT_MAX_LENGTH) {
    sanitized = sanitized.substring(0, USER_AGENT_MAX_LENGTH);
  }

  // Trim whitespace
  sanitized = sanitized.trim();

  // Return undefined if empty after sanitization
  if (sanitized.length === 0) {
    return undefined;
  }

  return sanitized;
}

/**
 * Maximum length for IP address (IPv6 max length is 45 characters).
 * Used for IP address sanitization to prevent log injection.
 */
const IP_MAX_LENGTH = 45;

/**
 * Extract and sanitize IP address from request headers.
 * Handles x-forwarded-for (takes first IP) and x-real-ip fallback.
 * Sanitizes to prevent log injection.
 *
 * Security concerns addressed:
 * - Log injection via newlines/carriage returns
 * - Extremely long IP strings (memory/log storage issues)
 * - Invalid IP formats that could break log parsing
 *
 * @param forwardedFor - Value from x-forwarded-for header (can be null/undefined)
 * @param realIp - Value from x-real-ip header (can be null/undefined)
 * @returns Sanitized IP address string or undefined if no valid IP found
 *
 * @example
 * ```typescript
 * const ip = sanitizeIpAddress(
 *   request.headers.get('x-forwarded-for'),
 *   request.headers.get('x-real-ip')
 * );
 * // Safe to log or send to Sentry
 * ```
 */
export function sanitizeIpAddress(
  forwardedFor: string | null | undefined,
  realIp: string | null | undefined
): string | undefined {
  // Remove newlines first, then extract IP from x-forwarded-for (take first IP if comma-separated list)
  const cleanedForwardedFor = forwardedFor?.replace(/[\r\n]+/g, '');
  const cleanedRealIp = realIp?.replace(/[\r\n]+/g, '');

  const ip = cleanedForwardedFor?.split(',')[0]?.trim() || cleanedRealIp?.trim() || undefined;

  if (!ip || typeof ip !== 'string') {
    return undefined;
  }

  // Additional sanitization: trim and ensure no remaining newlines
  let sanitized = ip.trim();

  // Truncate if too long (IPv6 max length is 45 chars)
  if (sanitized.length > IP_MAX_LENGTH) {
    sanitized = sanitized.substring(0, IP_MAX_LENGTH);
  }

  // Return undefined if empty after sanitization
  if (sanitized.length === 0) {
    return undefined;
  }

  return sanitized;
}
